import { useState, useEffect, useRef } from 'react';
import { careerServices } from '../../portfolio';
import EnrollmentModal from '../EnrollmentModal/EnrollmentModal';
import FloatingBubbles from '../FloatingBubbles/FloatingBubbles';

const CareerServices = () => {
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.08 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => { if (sectionRef.current) observer.unobserve(sectionRef.current); };
  }, []);

  const handleEnroll = (course) => {
    setSelectedCourse(course);
    setIsModalOpen(true);
  };

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const serviceImages = {
    'crypto-coaching': '/images/crypto.jpg',
    'faceless-ai': '/images/faceless.jpg',
    'graphics-design': '/images/graphics.jpg',
    'ugc-videos': '/images/ugc.jpg',
  };

  const cardTints = [
    'from-primary/25 via-violet/15 to-hotpink/15',
    'from-skyblue/25 via-primary/15 to-secondary/15',
    'from-secondary/20 via-violet/15 to-primary/15',
    'from-hotpink/20 via-accent/15 to-violet/15',
  ];

  return (
    <section id="career-services" ref={sectionRef} className="relative py-14 md:py-20 lg:py-24 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/90 via-primary/[0.04] to-violet-50/30 dark:from-slate-950 dark:via-slate-950 dark:to-slate-900" />
      <div className="absolute inset-0 bg-gradient-mesh opacity-35 dark:opacity-18 animate-gradientMesh" />
      <div className="absolute inset-0 bg-dot-grid opacity-25 dark:opacity-12 pointer-events-none" />

      <div className="absolute -top-20 right-0 w-80 h-80 rounded-full bg-hotpink/15 blur-3xl animate-aurora pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-primary/15 blur-3xl animate-aurora pointer-events-none" style={{ animationDelay: '-8s' }} />

      <FloatingBubbles count={6} color="gradient" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-10 md:mb-14 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0 blur-0' : 'opacity-0 translate-y-8 blur-md'}`}>
          <span className="btn--chip mb-5">
            <span>💼</span>
            Career Boost
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-4 font-space tracking-tighter-2">
            Career <span className="text-gradient-brand">Services</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Personalized 1-on-1 coaching and professional services to accelerate your career
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 md:gap-8">
          {careerServices.map((service, index) => {
            const isExpanded = expandedId === service.id;

            return (
              <div
                key={service.id}
                className={`tilt-3d-wrap transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0 blur-0' : 'opacity-0 translate-y-10 blur-md'}`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="tilt-3d h-full">
                  <div className="relative h-full card-modern border-gradient group overflow-hidden">
                    <div className={`absolute inset-0 bg-gradient-to-br ${cardTints[index % cardTints.length]} opacity-40 pointer-events-none z-0 transition-opacity duration-500 group-hover:opacity-70`} />

                    {/* Mobile collapsed header */}
                    <button
                      type="button"
                      onClick={() => toggleExpand(service.id)}
                      className="md:hidden relative z-10 w-full flex items-center gap-3 p-4 text-left"
                      aria-expanded={isExpanded}
                    >
                      <div className="w-14 h-14 rounded-2xl overflow-hidden flex-shrink-0 shadow-soft ring-2 ring-white/70">
                        {serviceImages[service.id] ? (
                          <img src={serviceImages[service.id]} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gradient-brand flex items-center justify-center text-2xl">
                            {service.icon}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-extrabold text-gray-900 font-space tracking-tight truncate">
                          {service.title}
                        </h3>
                        <p className="text-xs font-bold text-gradient-brand truncate">{service.tagline}</p>
                      </div>
                      <svg
                        className={`w-5 h-5 text-primary flex-shrink-0 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Desktop image (always) + mobile image when expanded */}
                    <div className={`${isExpanded ? 'block' : 'hidden'} md:block relative h-44 md:h-52 overflow-hidden tilt-layer-deep z-10`}>
                      {serviceImages[service.id] ? (
                        <img
                          src={serviceImages[service.id]}
                          alt={service.title}
                          className="w-full h-full object-cover transition-transform duration-[1200ms] ease-juicy group-hover:scale-115"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-brand flex items-center justify-center text-6xl">
                          {service.icon}
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      <div className="absolute inset-0 shine-overlay opacity-60 pointer-events-none" />
                      {index === 0 && (
                        <div className="absolute top-3 right-3 md:top-4 md:right-4 animate-popIn">
                          <div className="badge-gradient px-3 py-1 bg-gradient-brand text-white font-bold shadow-juicy border-0">
                            ⭐ Popular
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Details: collapsed on mobile until opened */}
                    <div className={`${isExpanded ? 'block' : 'hidden'} md:block relative p-5 md:p-7 z-10 tilt-layer-mid flex flex-col md:h-[calc(100%-13rem)]`}>
                      <h3 className="hidden md:block text-xl md:text-2xl font-extrabold text-gray-900 mb-1.5 font-space tracking-tight">
                        {service.title}
                      </h3>

                      <p className="hidden md:block text-sm md:text-base font-bold text-gradient-brand mb-3">
                        {service.tagline}
                      </p>

                      <p className="text-gray-600 mb-5 leading-relaxed text-sm md:text-base">
                        {service.description}
                      </p>

                      <div className="flex items-center justify-between text-sm md:text-base mb-4">
                        <span className="badge-gradient px-3 py-1">
                          {service.duration}
                        </span>
                        <span className="font-extrabold text-2xl md:text-3xl text-gradient-brand font-space tracking-tight">
                          ₦{service.price.toLocaleString()}
                        </span>
                      </div>

                      <ul className="space-y-2 mb-6 flex-1">
                        {service.features.slice(0, 3).map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2.5 group/item">
                            <div className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-gradient-brand flex items-center justify-center shadow-soft group-hover/item:animate-wiggle">
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                            <span className="text-gray-600 text-sm md:text-base group-hover/item:text-gray-800 transition-colors duration-300">
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>

                      <div className="tilt-layer-near">
                        <button
                          onClick={() => handleEnroll(service)}
                          className="btn btn--primary w-full rounded-2xl text-sm md:text-base shine-overlay"
                        >
                          <span className="flex items-center justify-center gap-2">
                            Enroll Now
                            <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <EnrollmentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        course={selectedCourse}
      />
    </section>
  );
};

export default CareerServices;
